/**
 * Shared image deduplication utilities.
 *
 * Used by PlaceOrderContent (mount + upload), UserRoute (socket handlers),
 * and navigationUtils (loadUserData) to prevent duplicate images when
 * merging DB batch data with localStorage pending images.
 */

import {
  savePendingImages,
  clearPendingImages,
  hydratePendingImageForGrid,
} from './pendingImageStorage';
import { hasCroppedCloudinaryUrl } from './placeOrderImageUtils';

/**
 * Build a Set of all non-null identifiers from an array of images.
 * Works for both DB images and pending/local images.
 */
export const buildImageIdentifierSet = (images) => {
  const ids = new Set();
  images.forEach((img) => {
    if (img._id) ids.add(img._id);
    if (img.id) ids.add(img.id);
    if (img.publicId) ids.add(img.publicId);
    if (img.stagingKey) ids.add(img.stagingKey);
    if (img.sourceStagingKey) ids.add(img.sourceStagingKey);
    if (img.croppedStagingKey) ids.add(img.croppedStagingKey);
    if (img.url) ids.add(img.url);
    if (img.originalUrl) ids.add(img.originalUrl);
    if (img.filename) ids.add(img.filename);
    if (img.AIinfo?.original_filename) ids.add(img.AIinfo.original_filename);
    if (img.AIinfo?.original_s3_url) ids.add(img.AIinfo.original_s3_url);
    if (img.AIinfo?.public_id) ids.add(img.AIinfo.public_id);
  });
  return ids;
};

/**
 * Filter pending images to exclude any that match DB image identifiers.
 */
const filterPendingAgainstDb = (pendingImages, dbIdentifiers) => {
  return pendingImages.filter((p) => {
    const keys = [
      p.filename,
      p.originalUrl,
      p.url,
      p.id,
      p._id,
      p.publicId,
      p.stagingKey,
      p.sourceStagingKey,
      p.croppedStagingKey,
    ].filter(Boolean);

    return !keys.some((key) => dbIdentifiers.has(key));
  });
};

/**
 * Reconcile a finished upload batch with the live grid.
 *
 * A DB refresh can land while a batch is still uploading (a slow file holds
 * the batch open while its siblings have already finalized). The refreshed
 * grid then already holds those siblings as bare DB rows, and treating the
 * batch copies as duplicates dropped their blob preview and auto-crop — the
 * photo looked "not uploaded" and never got cropped. Instead, a batch row that
 * matches a live row by server identity REPLACES it (keeping the live row's
 * ordering/DB fields); only rows that repeat within the batch, or match a live
 * row by filename/url without any server key, are true duplicates.
 *
 * @returns {{ merged: Array, added: Array, replaced: Array, duplicates: number }}
 */
export const reconcileIncomingUploads = (currentImages, incoming) => {
  const liveByKey = new Map();
  (currentImages || []).forEach((img) => {
    [img._id, img.id, img.stagingKey, img.publicId, img.sourceStagingKey]
      .filter(Boolean)
      .forEach((key) => liveByKey.set(String(key), img));
  });

  const seen = buildImageIdentifierSet(currentImages || []);
  const replacements = new Map();
  const added = [];
  let duplicates = 0;

  (incoming || []).forEach((img) => {
    const primaryKeys = [img.stagingKey, img.publicId].filter(Boolean).map(String);

    if (primaryKeys.length > 0) {
      const live = primaryKeys.map((key) => liveByKey.get(key)).find(Boolean);
      if (live) {
        if (replacements.has(live)) {
          duplicates += 1;
          return;
        }
        replacements.set(live, {
          ...live,
          ...img,
          _id: live._id || img._id,
          id: live._id || live.id || img.id,
        });
        return;
      }
      if (primaryKeys.some((key) => seen.has(key))) {
        duplicates += 1;
        return;
      }
      primaryKeys.forEach((key) => seen.add(key));
      added.push(img);
      return;
    }

    const fallbackKeys = [img.filename, img.originalUrl, img.url].filter(Boolean);
    if (fallbackKeys.some((key) => seen.has(key))) {
      duplicates += 1;
      return;
    }
    fallbackKeys.forEach((key) => seen.add(key));
    added.push(img);
  });

  const merged = [
    ...(currentImages || []).map((img) => replacements.get(img) || img),
    ...added,
  ];

  return { merged, added, replaced: [...replacements.values()], duplicates };
};

/**
 * Keep blob/file handles from the live grid when a DB refresh would drop in-flight uploads.
 */
export const preserveInMemoryGridUploads = (merged, liveImages, sortByPageOrdering) => {
  if (!Array.isArray(liveImages) || liveImages.length === 0) {
    return merged;
  }

  const mergedKeys = buildImageIdentifierSet(merged);
  const liveByKey = new Map();

  liveImages.forEach((img) => {
    [img._id, img.id, img.stagingKey, img.publicId, img.sourceStagingKey]
      .filter(Boolean)
      .forEach((key) => liveByKey.set(String(key), img));
  });

  const enriched = (merged || []).map((row) => {
    const keys = [
      row._id,
      row.id,
      row.stagingKey,
      row.publicId,
      row.sourceStagingKey,
    ].filter(Boolean);
    const live = keys.map((key) => liveByKey.get(String(key))).find(Boolean);
    if (!live) return row;

    const cropPersisted =
      hasCroppedCloudinaryUrl(row) || hasCroppedCloudinaryUrl(live);
    const pendingLineartRerun =
      live.isCropped &&
      !live.AIUrl &&
      !live.AIinfo &&
      (live.croppedStagingKey || hasCroppedCloudinaryUrl(live));

    return {
      ...row,
      ...(live.blobUrl ? { blobUrl: live.blobUrl } : {}),
      // The server never stores the crop rectangle; keep the one measured
      // here so the crop tool reopens on it.
      ...(live.cropData && !row.cropData ? { cropData: live.cropData } : {}),
      ...(live.cropReview && !row.cropReview ? { cropReview: live.cropReview } : {}),
      ...(live.file instanceof Blob ? { file: live.file } : {}),
      ...(live.originalFile instanceof Blob
        ? { originalFile: live.originalFile }
        : {}),
      ...(live.croppedStagingKey ? { croppedStagingKey: live.croppedStagingKey } : {}),
      ...(live.croppedCloudinaryUrl
        ? { croppedCloudinaryUrl: live.croppedCloudinaryUrl }
        : {}),
      ...(live.lineartInputStagingKey
        ? { lineartInputStagingKey: live.lineartInputStagingKey }
        : {}),
      ...(typeof live.cropUploadFailed === 'boolean'
        ? { cropUploadFailed: live.cropUploadFailed }
        : {}),
      ...(live.nextSaveFailures && !cropPersisted ? { nextSaveFailures: live.nextSaveFailures } : {}),
      ...(typeof live.prepVersion === 'number' ? { prepVersion: live.prepVersion } : {}),
      ...(typeof live.isCropped === 'boolean' ? { isCropped: live.isCropped } : {}),
      ...(pendingLineartRerun
        ? { AIUrl: null, AIinfo: null, isGenerating: false }
        : {}),
      isLoadingCover: cropPersisted
        ? false
        : typeof live.isLoadingCover === 'boolean'
          ? live.isLoadingCover
          : row.isLoadingCover,
      ...(cropPersisted ? { cropUploadFailed: false } : {}),
    };
  });

  const inFlight = liveImages.filter((img) => {
    if (img.AIUrl || img.AIinfo) return false;

    const keys = [
      img._id,
      img.id,
      img.stagingKey,
      img.publicId,
      img.sourceStagingKey,
    ].filter(Boolean);

    if (keys.length === 0) {
      return !!(img.blobUrl || img.file);
    }

    return !keys.some((key) => mergedKeys.has(key));
  });

  if (inFlight.length === 0) {
    return enriched;
  }

  const combined = [...enriched, ...inFlight];
  return typeof sortByPageOrdering === 'function'
    ? sortByPageOrdering(combined)
    : combined;
};

/** Identifiers a pending row can be matched on (same keys as filterPendingAgainstDb). */
const pendingIdentifiers = (p) =>
  [
    p.filename,
    p.originalUrl,
    p.url,
    p.id,
    p._id,
    p.publicId,
    p.stagingKey,
    p.sourceStagingKey,
    p.croppedStagingKey,
  ].filter(Boolean);

/**
 * A DB row replaces the pending copy of the same photo after a reload, but
 * the server never stores the crop rectangle, so copy it across from the
 * pending copy. Rows that already carry one, or match nothing, are untouched.
 */
export const carryPendingCropData = (dbImages, pendingImages) => {
  const cropDataByKey = new Map();
  (pendingImages || []).forEach((p) => {
    // A photo the auto crop couldn't cut has no box, only its "Worth a look" flag.
    if (!p || (!p.cropData && !p.cropReview)) return;
    pendingIdentifiers(p).forEach((key) => cropDataByKey.set(String(key), { cropData: p.cropData, cropReview: p.cropReview || null }));
  });
  if (cropDataByKey.size === 0) return dbImages;

  return dbImages.map((row) => {
    if (row.cropData) return row;
    const ids = buildImageIdentifierSet([row]);
    for (const id of ids) {
      const carried = cropDataByKey.get(String(id));
      if (carried) return { ...row, cropData: carried.cropData || null, cropReview: row.cropReview || carried.cropReview };
    }
    return row;
  });
};

/**
 * Merge DB images with localStorage pending images, deduplicating by identifiers.
 * DB images take priority. Updates localStorage with only the remaining pending images.
 *
 * @param {Array} dbImages - Images from fetchBatchImagesByBookId (non-cover)
 * @param {Array} pendingImages - Images from loadPendingImages
 * @param {string} bookId - Active book ID (for updating localStorage)
 * @returns {Array} Merged, deduplicated image array
 */
export const mergeDbAndPending = (dbImages, pendingImages, bookId) => {
  console.log(`[IMAGE_DEBUG] Merging images for book: ${bookId}`);
  console.log(`[IMAGE_DEBUG] DB images count: ${dbImages.length}`, dbImages.map(img => img._id || img.id));
  console.log(`[IMAGE_DEBUG] Pending images count: ${pendingImages.length}`, pendingImages.map(img => img.id || img._id));

  if (!pendingImages.length) {
    console.log(`[IMAGE_DEBUG] No pending images, returning ${dbImages.length} DB images`);
    return dbImages;
  }
  if (!dbImages.length) {
    console.log(`[IMAGE_DEBUG] No DB images, returning ${pendingImages.length} pending images`);
    return pendingImages.map(hydratePendingImageForGrid);
  }

  const dbIdentifiers = buildImageIdentifierSet(dbImages);
  const stillPending = filterPendingAgainstDb(pendingImages, dbIdentifiers);
  const dbWithCropData = carryPendingCropData(dbImages, pendingImages);

  console.log(`[IMAGE_DEBUG] Deduplication complete. Still pending count: ${stillPending.length}`);

  // Keep localStorage in sync with only truly-pending images
  const sortKey = (img, fallback) => {
    if (typeof img.gridOrdering === 'number') return img.gridOrdering;
    if (typeof img.ordering === 'number') return img.ordering;
    return fallback;
  };

  const merged = [...dbWithCropData, ...stillPending.map(hydratePendingImageForGrid)]
    .map((img, i) => ({ ...img, _sortKey: sortKey(img, 1000 + i) }))
    .sort((a, b) => a._sortKey - b._sortKey)
    .map(({ _sortKey, ...img }) => img);

  if (stillPending.length > 0) {
    savePendingImages(bookId, merged);
  } else {
    console.log(`[IMAGE_DEBUG] All pending images matched DB, clearing localStorage pending`);
    clearPendingImages(bookId);
  }

  console.log(`[IMAGE_DEBUG] Final merged count: ${merged.length}`);
  return merged;
};
